<template>
    <BaseSection 
        class="section--wide"
    >
        <template #title>
            <slot name="title">
                <h2 :id="kebabCase(title)">{{ title }}</h2>
            </slot>
        </template>

        <template
            v-if="subtitle"
            #subtitle
        >
            <p>{{ subtitle ?? '' }}</p>
        </template>

        <template #action>
            <nuxt-link
                :to="link ? link : '/news'"
                aria-label="See all news"
            >
                See all <BaseIcon icon="chevron-right" />
            </nuxt-link>
        </template>

        <BaseCardCollection
            :cards="cards"
            :show-meta="!hideMeta"
            :placeholder-count="3"
        />
    </BaseSection>
</template>

<script setup lang="ts">
import { kebabCase } from 'lodash-es';
import RelatedNewsQuery from '~/graphql/queries/news/Related.graphql';
import OverviewMetaQuery from '~/graphql/queries/news/OverviewMeta.graphql';
import OverviewCrossSiteQuery from '~/graphql/queries/news/OverviewCrossSite.graphql';
import useSrcSets from '~/utils/srcset';

const { getSrcSet } = useSrcSets();
const { activeSite } = useMultisite();
const { variables: craftVariables } = useCraftVariables();
const nuxtApp = useNuxtApp();

type ArticleImage = {
    large: string;
    medium: string;
    small: string;
};

type Article = {
    id: string;
    title: string;
    postDate: string;
    image: ArticleImage[];
    uri: string;
    url?: string;
    siteHandle?: string;
    crossPublicationCategories?: Array<{ id: string | number }>;
    isCrossPublished?: boolean;
    handle?: string;
};
type Props = {
    title: string;
    subtitle?: string;
    tags: Array<{ id: string; name: string }>;
    news: Article[];
    link?: string;
    hideMeta?: boolean;
    isPatientUpdates?: boolean;
};

const props = defineProps<Props>();
const route = useRoute();

const cardLimit = computed(() => route.name === 'related-content' ? 4 : 3);

const filledNews = ref<Article[]>([]);
const loading = ref(true);

const hasTag = Boolean(props.tags?.length);

watch([props.tags, props.news, activeSite], async function() {
    const destructed = cardLimit.value;
    filledNews.value = hasTag? [] : (props.news || []);

    // Exclude news items already added manually
    const excludeIds = hasTag? [] : (props.news ? props.news.map(({ id }) => id) : []);

    // Check if the new news articles need to be related by tags
    const tagIds = computed(() => props.tags?.map(({ id }) => id) || null);

    let variables = {};

    // If less than 3 or 4 news articles were passed, collect the rest
    if (route.name === 'topics-slug' && (!props.news || props.news.length < cardLimit.value)) {
        const limit = props.news.length ? cardLimit.value - props.news.length : cardLimit.value;

        variables = {
            limit,
            exclude: ['not', ...excludeIds],
            site: activeSite?.value?.handle,
            relatedToEntries: [
                {
                    slug: route.params.slug,
                    type: 'topic'
                }
            ]
        };
    }
    //If less than or equal to 3 or 4 news articles found, collect the rest
    else if (filledNews.value.length <= destructed) {
        // Check how many more news articles need to be queried
        const limit = filledNews.value.length ? destructed - filledNews.value.length : destructed;

        variables = {
            limit,
            exclude: ['not', ...excludeIds],
            site: activeSite?.value?.handle,
            tagIds: tagIds.value
        };
    }

    const { data } = await useAsyncQuery({
        query: RelatedNewsQuery,
        variables
    });

    loading.value = false;

    if (data.value && data.value.news) {
        filledNews.value = filledNews.value.concat(data.value.news);
    }

    // Cross-published news from sibling sites, based on the site-wide
    // configured news categories (same source as the /news overview).
    const { data: metaData } = await nuxtApp.runWithContext(() => useAsyncQuery({
        query: OverviewMetaQuery,
        variables: { site: craftVariables.site }
    }));

    const categoryIds: Array<string | number> =
        metaData.value?.overview?.overviewNewsCategory?.map((cat: { id: string | number }) => cat.id) ?? [];

    if (categoryIds.length && !hasTag) {
        const { data: crossData } = await nuxtApp.runWithContext(() => useAsyncQuery({
            query: OverviewCrossSiteQuery,
            variables: {
                offset: 0,
                limit: destructed,
                relatedTo: categoryIds.map(Number)
            }
        }));

        const crossPosts: Article[] = (crossData.value?.entries ?? [])
            .filter((entry: Article) =>
                entry.crossPublicationCategories?.some(cat => categoryIds.includes(cat.id))
            )
            .map((item: Article) => ({
                ...item,
                isCrossPublished: item.siteHandle !== activeSite?.value?.handle,
                handle: item.siteHandle
            }));

        filledNews.value = filledNews.value.concat(crossPosts);
    }

    // Remove duplicates by id, show most recent first, cap at the card limit
    const pinned = filledNews.value.filter(item => excludeIds.includes(item.id));
    const rest = filledNews.value
        .filter(item => !excludeIds.includes(item.id))
        .filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
        .sort((a, b) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime());

    filledNews.value = pinned.concat(rest).slice(0, destructed);
}, { immediate: true });


const { formatSingleDate, formats } = useDate();
const { getCardPills } = usePillsCard();

const cards = computed(() => {
    if (filledNews.value && Array.isArray(filledNews.value)) {
        return filledNews.value.map((news) => {
            let image = null;
            if (news.image.length) {
                image = {
                    src: news.image?.[0]?.large,
                    srcset: getSrcSet(news.image?.[0])
                };
            }
            return {
                image,
                title: news.title,
                meta: formatSingleDate(news.postDate, formats.dayMonthYear),
                pills: props.isPatientUpdates ? [{ text: 'Update' }] : getCardPills(news),
                uri: news.uri,
                url: news.url,
                isCrossPublished: news.isCrossPublished,
                handle: news.handle
            };
        });
    }
    return [];
});
</script>
